import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AirplaneTakeoff, CaretDown, CaretUp, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import StickyFlightFields from "./StickyFlightFields";
import InspirePopover from "./InspirePopover";

const LS_KEY = "jl_sticky_flight_collapsed";
const LS_ORIGIN = "jl_sticky_origin";
const LS_ORIGIN_META = "jl_sticky_origin_meta";

const readOriginMeta = () => {
  try { return JSON.parse(localStorage.getItem(LS_ORIGIN_META) || "null"); } catch { return null; }
};

/**
 * Site-wide compact flight search bar. Orchestrates the fields + inspire popover,
 * handles geo auto-prefill, collapse/dismiss UI state, and form submission to /booking.
 */
export default function StickyFlightBar() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(LS_KEY) === "1");
  const [dismissed, setDismissed] = useState(false);
  const [origin, setOrigin] = useState(() => localStorage.getItem(LS_ORIGIN) || "");
  const [originMeta, setOriginMeta] = useState(readOriginMeta);
  const [originTouched, setOriginTouched] = useState(false);
  const [destination, setDestination] = useState("");
  const [depart, setDepart] = useState("");

  useEffect(() => { localStorage.setItem(LS_KEY, collapsed ? "1" : "0"); }, [collapsed]);

  // Geo-IP auto-prefill: only runs once on mount when origin isn't set.
  useEffect(() => {
    if (origin || originTouched) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/geo/airport");
        if (cancelled || !data?.iata) return;
        setOrigin(data.iata);
        const meta = { city: data.city, country: data.country_name || data.country, confidence: data.confidence };
        setOriginMeta(meta);
        localStorage.setItem(LS_ORIGIN, data.iata);
        localStorage.setItem(LS_ORIGIN_META, JSON.stringify(meta));
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("Geo prefill unavailable:", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (location.pathname.startsWith("/booking") || location.pathname.startsWith("/admin")) return null;
  if (dismissed) return null;

  const handleOriginChange = (v) => {
    setOrigin(v);
    setOriginTouched(true);
    localStorage.setItem(LS_ORIGIN, v);
  };

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (origin) params.set("origin_iata", origin.trim().toUpperCase());
    if (destination) params.set("destination_iata", destination.trim().toUpperCase());
    if (depart) params.set("depart_date", depart);
    params.set("flightSearch", "true");
    window.location.href = `/booking?${params.toString()}`;
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div
      className="bg-[#EFECE5] text-[#1C1A17] border-t border-[#D6CFC4]"
      data-testid="sticky-flight-bar"
      style={{ boxShadow: "inset 0 1px 0 rgba(180,83,60,0.35)" }}
    >
      <div className="container-page py-2.5">
        {collapsed ? (
          <CollapsedBar onExpand={() => setCollapsed(false)} />
        ) : (
          <form onSubmit={submit} className="flex items-center gap-3 flex-wrap md:flex-nowrap relative" data-testid="sticky-flight-form">
            <BrandChip />

            <StickyFlightFields
              origin={origin}
              originMeta={originMeta}
              originTouched={originTouched}
              onOriginChange={handleOriginChange}
              destination={destination}
              onDestinationChange={setDestination}
              depart={depart}
              onDepartChange={setDepart}
              today={today}
            />

            <div className="flex-1" />

            <InspirePopover origin={origin} onPickDeal={setDestination} />

            <button
              type="submit"
              className="shrink-0 bg-[#B4533C] text-[#F9F9F6] px-4 py-1.5 text-[11px] tracking-wider uppercase font-mono hover:bg-[#8F402D] transition-colors"
              data-testid="sticky-search-btn"
            >
              Search →
            </button>

            <button type="button" onClick={() => setCollapsed(true)} className="shrink-0 opacity-60 hover:opacity-100 p-1 text-[#1C1A17]" aria-label="Minimize" data-testid="sticky-collapse-btn">
              <CaretUp size={14} />
            </button>
            <button type="button" onClick={() => setDismissed(true)} className="shrink-0 opacity-60 hover:opacity-100 p-1 text-[#1C1A17]" aria-label="Dismiss" data-testid="sticky-dismiss-btn">
              <X size={14} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function CollapsedBar({ onExpand }) {
  return (
    <button
      onClick={onExpand}
      className="w-full flex items-center justify-between gap-3 py-0.5 text-xs hover:text-[#B4533C] transition-colors"
      data-testid="sticky-flight-expand"
    >
      <span className="flex items-center gap-2">
        <AirplaneTakeoff size={14} weight="regular" className="text-[#B4533C]" />
        <span className="overline text-[10px] text-[#B4533C]">Flight search</span>
        <span className="font-mono text-[11px] text-[#4A4742]">Open the mini search →</span>
      </span>
      <CaretDown size={14} />
    </button>
  );
}

function BrandChip() {
  return (
    <div className="flex items-center gap-2 pr-2 border-r border-[#D6CFC4]">
      <AirplaneTakeoff size={15} weight="regular" className="text-[#B4533C] shrink-0" />
      <span className="overline text-[10px] hidden md:inline whitespace-nowrap text-[#B4533C]">Find flights</span>
    </div>
  );
}
